/* eslint-disable react-hooks/exhaustive-deps */
import React, { createRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import SearchGridWrapper from 'app/layout/pages/SearchGridWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { revalidateChipSelection } from '@AMIEWEB/GlobalSearch/helper';
import { selectFilters, selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { RootState } from 'types';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { defaultFilterValues } from '@AMIEWEB/Order/OrderDetails/PayPackages/PlacementPayPackages/filterUtils';
import { PlacementPayPackagesChips } from '@AMIEWEB/Order/OrderDetails/PayPackages/PlacementPayPackages/PlacementPayPackagesChips';
import { usePlacementPayPackagesFetch } from '@AMIEWEB/Order/OrderDetails/PayPackages/PlacementPayPackages/usePlacementPayPackagesFetch';
import { FacilityPlacementPayPackagesFilters } from './FacilityPlacementPayPackagesFilters';
import { FacilityPlacementPayPackagesGrid } from './FacilityPlacementPayPackagesGrid';

export const FacilityPlacementPayPackagesWrapper = ({ facilityId }: { facilityId: number }) => {
  const dispatch = useDispatch();
  const fetchPlacementPayPackages = usePlacementPayPackagesFetch({ facilityId });
  const filterPreferencesId = `facility-placement-pay-packages-filter-${facilityId}`;

  const formMethods = useForm({ defaultValues: defaultFilterValues });
  const { handleSubmit, reset } = formMethods;

  const gridState = useSelector(selectGridState);
  const appliedFilterValues = useSelector(selectFilters);

  const filterPreference = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === filterPreferencesId);
  });
  const { loaded: preferencesLoaded } = useSelector(selectUserPreference);

  const hiddenSubmitButton = createRef<HTMLButtonElement>();

  const onApplyFilter = (filterValues, newSelectedChipFilters?) => {
    dispatch(gridStateActions.setPageNumber(1));
    const selectedChipFilters =
      newSelectedChipFilters || revalidateChipSelection(filterValues, gridState.filterAttributes?.selectedChipFilters || []);
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(filterValues ?? defaultFilterValues),
        selectedChipFilters,
      }),
    );
    fetchPlacementPayPackages({
      pageNumber: 1,
      filters: filterValues ?? defaultFilterValues,
      selectedChipFilters,
    });
  };

  const onApplyFilterAndSave = (filterValues, newSelectedChipFilters?) => {
    const preferenceData = {
      id: filterPreferencesId,
      value: {
        filterValues: filterValues,
        newSelectedChipFilters: newSelectedChipFilters ?? appliedFilterValues?.selectedChipFilters,
      },
    };
    onApplyFilter(preferenceData.value.filterValues, newSelectedChipFilters);
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
  };

  const saveFilterPreferences = (filterValues, newSelectedChipFilters?) => {
    onApplyFilterAndSave(filterValues, newSelectedChipFilters);
  };

  useEffect(() => {
    if (preferencesLoaded) {
      const payload = filterPreference?.value ?? { filterValues: defaultFilterValues };
      reset(payload?.filterValues);
      onApplyFilter(payload?.filterValues, payload?.newSelectedChipFilters);
    }
  }, [facilityId, filterPreference, preferencesLoaded]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(filter => onApplyFilterAndSave(filter))}>
        <SearchGridWrapper
          filters={
            <FacilityPlacementPayPackagesFilters
              facilityId={facilityId}
              saveFilterPreferences={saveFilterPreferences}
              onRefresh={() => fetchPlacementPayPackages({ pageNumber: 1, pageSize: 20 })}
            />
          }
          table={<FacilityPlacementPayPackagesGrid facilityId={facilityId} />}
          chips={<PlacementPayPackagesChips onApplyFilter={onApplyFilterAndSave} />}
          tableMinHeight={430}
          loading={false}
        />
        <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
      </form>
    </FormProvider>
  );
};
